import { useState } from 'react';
import { Printer } from 'lucide-react';
import { usePropertyReceiptQuery } from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { Alert, Button, ErrorState, LoadingState, Modal } from '@/components/ui';
import { printDocument } from '@/lib/printing';
import { propertyReceiptDocument } from '@/lib/receiptDocuments';

/**
 * A property sale receipt, laid out as the thermal printer will print it.
 *
 * The lines come from the API — the same payload the Python driver takes on
 * stdin — so what is on screen and what comes out of the printer cannot drift
 * apart. Nothing here reformats a figure; the server has already rounded.
 */
export function PropertySaleReceipt({
  saleId,
  paymentId,
  onClose,
}: {
  saleId: string;
  paymentId?: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError, error, refetch } = usePropertyReceiptQuery({ saleId, paymentId });
  const [printing, setPrinting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  /**
   * Laid out as blocks like every other receipt.
   *
   * The driver understands this payload directly, but a phone printing over
   * Bluetooth needs finished bytes, and those are rendered from blocks — so
   * every route prints the same receipt.
   */
  const print = async (): Promise<void> => {
    if (!data) return;
    setFailure(null);
    setPrinting(true);
    try {
      const result = await printDocument(propertyReceiptDocument(data as Record<string, any>));
      if (!result.printed) setFailure(result.error);
    } finally {
      setPrinting(false);
    }
  };

  const payload = data as Record<string, any> | undefined;
  const business = payload?.business ?? {};
  const sale = payload?.sale ?? {};
  const property = payload?.property ?? {};
  const buyer = payload?.buyer ?? {};
  const payment = payload?.payment as Record<string, string> | null | undefined;
  const history = (payload?.history ?? []) as { index: number; amount: string; receivedAt: string }[];

  const currency = payload?.currency ?? {};
  const asMoney = (value?: string): string => {
    if (value === undefined) return '';
    const amount = Number(value);
    const body = Number.isNaN(amount)
      ? value
      : amount.toLocaleString(undefined, {
          minimumFractionDigits: currency.decimals ?? 0,
          maximumFractionDigits: currency.decimals ?? 0,
        });
    return currency.position === 'AFTER' ? `${body}${currency.symbol ?? ''}` : `${currency.symbol ?? ''}${body}`;
  };

  const line = (label: string, value?: string | null) =>
    value ? (
      <div className="flex justify-between gap-2">
        <span>{label}</span>
        <span className="text-right">{value}</span>
      </div>
    ) : null;

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Receipt"
      description={sale.number ? `${sale.number} · ${buyer.name ?? ''}` : undefined}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            icon={Printer}
            disabled={!payload}
            loading={printing}
            onClick={() => void print()}
          >
            Print
          </Button>
        </div>
      }
    >
      {failure && <div className="mb-3"><Alert tone="danger">{failure}</Alert></div>}

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : (
        <div
          id="receipt-print-area"
          /* 58mm of paper is 32 characters; this width matches it on screen. */
          className="mx-auto max-w-[58mm] bg-white p-3 font-mono text-[10px] leading-snug text-black"
        >
          <div className="text-center">
            <p className="text-xs font-bold uppercase">{business.name}</p>
            {business.address && <p>{business.address}</p>}
            {business.phone && <p>{business.phone}</p>}
            {business.taxNumber && <p>{business.taxLabel}: {business.taxNumber}</p>}
          </div>

          <hr className="my-2 border-dashed border-black" />
          <p className="text-center font-bold">PROPERTY SALE RECEIPT</p>
          <hr className="my-2 border-dashed border-black" />

          {line('Receipt', sale.number)}
          {line('Buyer', buyer.name)}
          {line('Phone', buyer.phone)}

          <hr className="my-2 border-dashed border-black" />
          <p className="font-bold">{property.reference} {property.name}</p>
          {line('Title no.', property.titleNumber)}
          {line('Plot', property.plotNumber)}
          {line('Block', property.blockNumber)}
          {line('Area', property.area)}
          {line('Location', property.location)}
          {line('District', property.district)}

          {payment && (
            <>
              <hr className="my-2 border-dashed border-black" />
              {line('Paid now', asMoney(payment.amount))}
              {line('Method', payment.method)}
              {line('Ref', payment.reference ?? undefined)}
            </>
          )}

          <hr className="my-2 border-dashed border-black" />
          {line('Price', asMoney(sale.price))}
          {line('Paid to date', asMoney(sale.paid))}

          <hr className="my-2 border-black" />
          <p className="text-center">BALANCE DUE</p>
          <p className="text-center text-base font-bold">{asMoney(sale.balance)}</p>
          <hr className="my-2 border-black" />

          {history.length > 1 && (
            <>
              <p className="text-center">Payments so far</p>
              {history.map((entry) => (
                <div key={entry.index} className="flex justify-between gap-2">
                  <span>{entry.index}. {entry.receivedAt.slice(0, 10)}</span>
                  <span>{asMoney(entry.amount)}</span>
                </div>
              ))}
              <hr className="my-2 border-dashed border-black" />
            </>
          )}

          <p className="text-center">{business.footer ?? 'Thank you.'}</p>
          {/* A reprint must say so, or a duplicate can pass as a second payment. */}
          <p className="mt-1 text-center text-[9px]">
            Printed {new Date().toLocaleString()}
          </p>
        </div>
      )}
    </Modal>
  );
}
